/**
 * SignalR-based Notification Service
 * Handles real-time notifications via SignalR WebSocket connection to /notificationHub
 */

import apiService from './api.jsx';
import { HubConnectionBuilder, LogLevel } from '@microsoft/signalr';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '';

class NotificationService {
  constructor() {
    this.listeners = new Map();
    this.connection = null;
    this.isConnected = false;
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 5;
    this.reconnectDelay = 1000; // Start witπh 1 second
    this.maxReconnectDelay = 30000; // Max 30 seconds
    this.seenNotificationIds = new Set();
    // Polling fallback properties
    this.pollingInterval = null;
    this.isPolling = false;
  }

  getHubUrl() {
    if (!API_BASE_URL) {
      return '/notificationHub';
    }

    return `${API_BASE_URL.replace(/\/api\/?$/, '')}/notificationHub`;
  }

  /**
   * Initialize SignalR connection to /notificationHub
   */
  async connect() {
    try {
      const authToken = this.getAuthToken();
      if (!authToken) {
        return false;
      }

      this.connection = new HubConnectionBuilder()
        .withUrl(this.getHubUrl(), {
          accessTokenFactory: () => this.getAuthToken() || '',
          withCredentials: false
        })
        .withAutomaticReconnect({
          nextRetryDelayInMilliseconds: (retryContext) => {
            const delay = Math.min(
              this.reconnectDelay * Math.pow(2, retryContext.previousRetryCount),
              this.maxReconnectDelay
            );
            return delay + Math.random() * 1000;
          }
        })
        .configureLogging(LogLevel.Warning)
        .build();

      this.setupEventHandlers();
      await this.connection.start();

      this.isConnected = true;
      this.reconnectAttempts = 0;
      this.reconnectDelay = 1000;
      this.stopPolling();

      return true;
    } catch {
      this.isConnected = false;
      this.connection = null;

      // Fallback to API polling if SignalR fails
      try {
        await this.startPolling();
        return true;
      } catch {
        return false;
      }
    }
  }

  /**
   * Set up SignalR event handlers (READ-ONLY)
   */
  setupEventHandlers() {
    if (!this.connection) return;

    // Handle new notifications (READ-ONLY)
    this.connection.on('ReceiveNotification', (notification) => {      
      this.emit('notification', notification);
    });

    // Handle connection events
    this.connection.onclose((error) => {
      this.isConnected = false;
      this.emit('connectionClosed', error);
    });

    this.connection.onreconnecting((error) => {
      this.isConnected = false;
      this.reconnectAttempts++;
      this.emit('reconnecting', { error, attempt: this.reconnectAttempts });
    });

    this.connection.onreconnected((connectionId) => {
      this.isConnected = true;
      this.reconnectAttempts = 0;
      this.reconnectDelay = 1000; // Reset delay
      this.emit('reconnected', connectionId);
    });
  }

  /**
   * Start polling for new notifications (fallback when SignalR is not available)
   */
  async startPolling() {
    if (this.isPolling) {
      return;
    }

    this.isPolling = true;

    // Initial fetch
    await this.fetchNotifications();

    // Set up polling interval
    this.pollingInterval = setInterval(async () => {
      await this.fetchNotifications();
    }, 30000); // 30 seconds
  }

  /**
   * Stop polling for notifications
   */
  stopPolling() {
    if (this.pollingInterval) {
      clearInterval(this.pollingInterval);
      this.pollingInterval = null;
    }
    this.isPolling = false;
  }
  async fetchNotifications() {
    try {
      const authToken = this.getAuthToken();
      if (!authToken) {
        return [];
      }

      const notifications = await apiService.get('/Notifications', {
        requireAuth: true
      });

      // Process new notifications
      this.processNotifications(notifications);

      return notifications;
    } catch {
      return [];
    }
  }

  /**
   * Process notifications and emit events for new ones
   */
  processNotifications(notifications) {
    if (!Array.isArray(notifications)) {
      return;
    }

    notifications.forEach((notification) => {
      if (!notification?.id) {
        return;
      }

      if (!this.seenNotificationIds.has(notification.id)) {
        this.seenNotificationIds.add(notification.id);
        this.emit('notification', notification);
      }
    });
  }

  /**
   * Disconnect from SignalR notification service
   */
  async disconnect() {
    try {

      if (this.connection) {
        await this.connection.stop();
        this.connection = null;
      }

      // Also stop polling if it's running
      this.stopPolling();

      this.isConnected = false;
      this.reconnectAttempts = 0;

      return true;
    } catch {
      return false;
    }
  }

  /**
   * Get connection status
   */
  getConnectionStatus() {
    return {
      isConnected: this.isConnected,
      connectionId: this.connection?.connectionId || null,
      state: this.isConnected ? 'Connected' : this.isPolling ? 'Polling' : 'Disconnected',
      isWebSocketDisabled: false, // SignalR handles WebSocket fallback
      reconnectAttempts: this.reconnectAttempts,
      maxReconnectAttempts: this.maxReconnectAttempts,
      transport: this.connection?.transport?.transportType || 'Unknown',
      isPolling: this.isPolling,
      pollingInterval: this.pollingInterval ? 30000 : null
    };
  }

  /**
   * Mark all notifications as read
   */
  async markAllAsRead() {
    const response = await apiService.post('/Notifications/markallasread', null, {
      requireAuth: true
    });

    // Emit event to notify listeners that all notifications have been marked as read
    this.emit('allNotificationsRead', response);

    return response;
  }

  /**
   * Send notification via SignalR (if connected) - READ ONLY
   * Note: This service is read-only, no CRUD operations
   */
  async sendNotificationViaSignalR() {
    // Read-only service - no sending notifications
    return false;
  }

  /**
   * Get auth token from localStorage
   */
  getAuthToken() {
    try {
      const authData = JSON.parse(localStorage.getItem('auth') || '{}');
      return authData?.token || authData?.accessToken;
    } catch {
      return null;
    }
  }

  /**
   * Request browser notification permission
   */
  async requestNotificationPermission() {
    if ('Notification' in window) {
      const permission = await Notification.requestPermission();
      return permission === 'granted';
    }
    return false;
  }

  /**
   * Show browser notification
   */
  async showBrowserNotification(notification) {
    if ('Notification' in window && Notification.permission === 'granted') {
      const browserNotification = new Notification(notification.title || 'New Notification', {
        body: notification.message || notification.content,
        icon: '/favicon.ico',
        tag: notification.id
      });

      browserNotification.onclick = () => {
        window.focus();
        browserNotification.close();
      };

      // Auto-close after 5 seconds
      setTimeout(() => {
        browserNotification.close();
      }, 5000);
    }
  }

  /**
   * Event listener management
   */
  on(event, callback) {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, []);
    }
    this.listeners.get(event).push(callback);
  }

  off(event, callback) {
    if (this.listeners.has(event)) {
      const callbacks = this.listeners.get(event);
      const index = callbacks.indexOf(callback);
      if (index > -1) {
        callbacks.splice(index, 1);
      }
    }
  }

  emit(event, data) {
    if (this.listeners.has(event)) {
      this.listeners.get(event).forEach((callback) => {
        try {
          callback(data);
        } catch {
          // Listener failures should not break other subscribers.
        }
      });
    }
  }
}

// Create singleton instance
const notificationService = new NotificationService();

export default notificationService;
